import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import TTIRequestLogViewer from '../components/TTIRequestLogViewer';
import { batchImportService } from '../services/batchImportService';
import { growerService } from '../services/growerService';
import { 
  BatchImport as BatchImportType,
  BatchImportItem,
  BatchImportItemUpdate,
  ClusterInfo,
  Grower,
} from '../types';

const BatchImportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const batchId = parseInt(id || '0', 10);
  
  // Batch import state
  const [batchImport, setBatchImport] = useState<BatchImportType | null>(null);
  const [items, setItems] = useState<BatchImportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Reference data
  const [clusters, setClusters] = useState<ClusterInfo[]>([]);
  const [growers, setGrowers] = useState<Grower[]>([]);
  
  // Edit state
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<BatchImportItemUpdate>({});
  const [saving, setSaving] = useState(false);
  
  // Execute state
  const [executing, setExecuting] = useState(false);
  const [executeError, setExecuteError] = useState('');

  // Retry state
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());

  // Logs modal state
  const [viewingLogsItemId, setViewingLogsItemId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    if (!batchId) return;
    
    setLoading(true);
    setError('');
    
    try {
      const [batchResponse, itemsResponse, clustersResponse, growersResponse] = await Promise.all([
        batchImportService.getBatchImport(batchId),
        batchImportService.getBatchImportItems(batchId, page, 50, statusFilter || undefined),
        batchImportService.getClusters(),
        growerService.getGrowers(),
      ]);
      
      setBatchImport(batchResponse);
      setItems(itemsResponse.items);
      setTotalPages(itemsResponse.total_pages);
      setTotalItems(itemsResponse.total);
      setClusters(clustersResponse);
      setGrowers(growersResponse);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load batch import');
    } finally {
      setLoading(false);
    }
  }, [batchId, page, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const startEditing = (item: BatchImportItem) => {
    setEditingItemId(item.id);
    setEditForm({
      device_id: item.original_device_id || item.device_id,
      dev_eui: item.dev_eui,
      join_eui: item.join_eui,
      app_key: item.app_key,
      device_name: item.device_name || '',
      description: item.description || '',
      sensor_type: item.sensor_type,
      cluster: item.cluster,
      grower_id: item.grower_id,
    });
  };

  const cancelEditing = () => {
    setEditingItemId(null);
    setEditForm({});
  };

  const saveItem = async () => {
    if (!editingItemId) return;
    
    setSaving(true);
    try {
      await batchImportService.updateBatchImportItem(batchId, editingItemId, editForm);
      setEditingItemId(null);
      setEditForm({});
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (itemId: number) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await batchImportService.deleteBatchImportItem(batchId, itemId);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete item');
    }
  };

  const revalidate = async () => {
    try {
      const result = await batchImportService.revalidateBatchImport(batchId);
      setBatchImport(result);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to revalidate');
    }
  };

  const executeImport = async () => {
    if (!window.confirm(`Are you sure you want to execute this import? This will register ${batchImport?.valid_items || 0} devices with TTI.`)) {
      return;
    }
    
    setExecuting(true);
    setExecuteError('');
    
    try {
      const result = await batchImportService.executeBatchImport(batchId);
      setBatchImport(result.batch_import);
      loadData();
    } catch (err: any) {
      setExecuteError(err.response?.data?.detail || 'Failed to execute import');
    } finally {
      setExecuting(false);
    }
  };

  const retryImport = async (itemIds?: number[]) => {
    const isSelective = itemIds && itemIds.length > 0;
    const count = isSelective ? itemIds.length : (batchImport?.failed_items || 0);
    if (!window.confirm(`Retry ${isSelective ? `${count} selected` : 'all failed'} item(s)?`)) return;

    setRetrying(true);
    setRetryError('');
    try {
      const result = await batchImportService.retryBatchImport(batchId, isSelective ? itemIds : undefined);
      setBatchImport(result.batch_import);
      setSelectedItemIds(new Set());
      loadData();
    } catch (err: any) {
      setRetryError(err.response?.data?.detail || 'Failed to retry import');
    } finally {
      setRetrying(false);
    }
  };

  const toggleItemSelection = (itemId: number) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };

  const toggleSelectAllFailed = () => {
    const failedIds = items.filter(i => i.status === 'failed').map(i => i.id);
    if (failedIds.every(id => selectedItemIds.has(id))) {
      setSelectedItemIds(prev => {
        const next = new Set(prev);
        failedIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedItemIds(prev => new Set(Array.from(prev).concat(failedIds)));
    }
  };

  const deleteBatch = async () => {
    if (!window.confirm('Are you sure you want to delete this batch import?')) return;
    
    try {
      await batchImportService.deleteBatchImport(batchId);
      navigate('/batch-imports');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete batch import');
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      valid: 'bg-green-100 text-green-800',
      invalid: 'bg-red-100 text-red-800',
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      skipped: 'bg-gray-100 text-gray-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading && !batchImport) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (!batchImport) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Batch import not found</p>
          <Link to="/batch-imports" className="text-primary-600 hover:text-primary-900 mt-4 inline-block">
            Back to Batch Imports
          </Link>
        </div>
      </Layout>
    );
  }

  const isPending = batchImport.status === 'pending';
  const isCompleted = batchImport.status === 'completed';
  const hasFailedItems = isCompleted && (batchImport.failed_items ?? 0) > 0;
  const canExecute = isPending && batchImport.valid_items > 0;
  const selectedFailed = items.filter(i => i.status === 'failed' && selectedItemIds.has(i.id));

  return (
    <Layout>
      <div className="space-y-6">
        {/* Breadcrumb & Header */}
        <div>
          <nav className="text-sm text-gray-500 mb-2">
            <Link to="/batch-imports" className="hover:text-primary-600">Batch Imports</Link>
            <span className="mx-2">/</span>
            <span>{batchImport.name || `Import #${batchImport.id}`}</span>
          </nav>
          
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                {batchImport.name || `Batch Import #${batchImport.id}`}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Application: {batchImport.app_id} • Created: {new Date(batchImport.created_at).toLocaleString()}
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusBadge(batchImport.status)}`}>
                {batchImport.status}
              </span>
              
              {isPending && (
                <>
                  <button
                    onClick={revalidate}
                    className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Revalidate
                  </button>
                  <button
                    onClick={deleteBatch}
                    className="px-4 py-2 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-50"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{batchImport.total_items}</div>
            <div className="text-sm text-gray-500">Total Items</div>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{batchImport.valid_items}</div>
            <div className="text-sm text-gray-500">Valid</div>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-2xl font-bold text-red-600">{batchImport.invalid_items}</div>
            <div className="text-sm text-gray-500">Invalid</div>
          </div>
          {batchImport.status === 'completed' && (
            <>
              <div className="bg-white shadow rounded-lg p-4">
                <div className="text-2xl font-bold text-green-600">{batchImport.successful_items}</div>
                <div className="text-sm text-gray-500">Successful</div>
              </div>
              <div className="bg-white shadow rounded-lg p-4">
                <div className="text-2xl font-bold text-red-600">{batchImport.failed_items}</div>
                <div className="text-sm text-gray-500">Failed</div>
              </div>
            </>
          )}
        </div>

        {/* Execute Section */}
        {isPending && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Execute Import</h2>
            
            {executeError && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                {executeError}
              </div>
            )}
            
            {batchImport.invalid_items > 0 ? (
              <div className="mb-4 p-3 bg-yellow-100 text-yellow-800 rounded">
                <strong>Warning:</strong> {batchImport.invalid_items} items have validation errors. 
                Fix them or delete them before executing.
              </div>
            ) : batchImport.valid_items === 0 ? (
              <div className="mb-4 p-3 bg-yellow-100 text-yellow-800 rounded">
                No valid items to import. Add or fix items before executing.
              </div>
            ) : (
              <div className="mb-4 p-3 bg-green-100 text-green-800 rounded">
                All {batchImport.valid_items} items are valid and ready to be imported.
              </div>
            )}
            
            <button
              onClick={executeImport}
              disabled={!canExecute || executing}
              className="px-6 py-3 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {executing ? 'Executing...' : `Execute Import (${batchImport.valid_items} devices)`}
            </button>
          </div>
        )}

        {/* Retry Section */}
        {hasFailedItems && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Retry Failed Items</h2>

            {retryError && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                {retryError}
              </div>
            )}

            <p className="text-sm text-gray-600 mb-4">
              {batchImport.failed_items} item(s) failed during the last import run.
              You can retry all of them, or select specific items in the table below and retry only those.
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => retryImport()}
                disabled={retrying}
                className="px-5 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {retrying ? 'Retrying...' : `Retry All Failed (${batchImport.failed_items})`}
              </button>

              {selectedFailed.length > 0 && (
                <button
                  onClick={() => retryImport(selectedFailed.map(i => i.id))}
                  disabled={retrying}
                  className="px-5 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {retrying ? 'Retrying...' : `Retry Selected (${selectedFailed.length})`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Filter items:</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All ({totalItems})</option>
              <option value="valid">Valid</option>
              <option value="invalid">Invalid</option>
              {batchImport.status === 'completed' && (
                <>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                </>
              )}
            </select>
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {hasFailedItems && (
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        title="Select all failed"
                        checked={items.filter(i => i.status === 'failed').length > 0 &&
                          items.filter(i => i.status === 'failed').every(i => selectedItemIds.has(i.id))}
                        onChange={toggleSelectAllFailed}
                        className="h-4 w-4 text-primary-600 rounded border-gray-300"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Row
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dev EUI
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cluster
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grower
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                  {(isPending || batchImport?.status === 'executing' || batchImport?.status === 'completed' || batchImport?.status === 'failed') && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      No items found.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id} className={`hover:bg-gray-50 ${item.status === 'invalid' ? 'bg-red-50' : ''}`}>
                      {editingItemId === item.id ? (
                        // Edit mode
                        <>
                          {hasFailedItems && <td className="px-4 py-3 w-10" />}
                          <td className="px-4 py-3 text-sm text-gray-500">{item.row_number}</td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={editForm.device_id || ''}
                              onChange={(e) => setEditForm({ ...editForm, device_id: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={editForm.dev_eui || ''}
                              onChange={(e) => setEditForm({ ...editForm, dev_eui: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-mono"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={editForm.cluster || 'eu1'}
                              onChange={(e) => setEditForm({ ...editForm, cluster: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              {clusters.map((cluster) => (
                                <option key={cluster.id} value={cluster.id}>
                                  {cluster.id}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={editForm.device_name || ''}
                              onChange={(e) => setEditForm({ ...editForm, device_name: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={editForm.grower_id || ''}
                              onChange={(e) => setEditForm({ ...editForm, grower_id: e.target.value ? parseInt(e.target.value) : null })}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="">None</option>
                              {growers.map((grower) => (
                                <option key={grower.id} value={grower.id}>
                                  {grower.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(item.status)}`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {item.validation_message || item.result_message}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={saveItem}
                                disabled={saving}
                                className="text-green-600 hover:text-green-900 text-sm"
                              >
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={cancelEditing}
                                className="text-gray-600 hover:text-gray-900 text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        // View mode
                        <>
                          {hasFailedItems && (
                            <td className="px-4 py-3 w-10">
                              {item.status === 'failed' && (
                                <input
                                  type="checkbox"
                                  checked={selectedItemIds.has(item.id)}
                                  onChange={() => toggleItemSelection(item.id)}
                                  className="h-4 w-4 text-primary-600 rounded border-gray-300"
                                />
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3 text-sm text-gray-500">{item.row_number}</td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900 font-mono">
                              {item.device_id}
                            </div>
                            {item.original_device_id && item.original_device_id !== item.device_id && (
                              <div className="text-xs text-gray-400">
                                Original: {item.original_device_id}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                            {item.dev_eui}
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                              {item.cluster}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {item.device_name || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {growers.find(g => g.id === item.grower_id)?.name || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(item.status)}`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate" title={item.validation_message || item.result_message || ''}>
                            {item.validation_message || item.result_message || '-'}
                          </td>
                          {isPending && (
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => startEditing(item)}
                                  className="text-primary-600 hover:text-primary-900 text-sm"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => deleteItem(item.id)}
                                  className="text-red-600 hover:text-red-900 text-sm"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          )}
                          {!isPending && (batchImport?.status === 'executing' || batchImport?.status === 'completed' || batchImport?.status === 'failed') && (
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => setViewingLogsItemId(item.id)}
                                className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                              >
                                📋 View Logs
                              </button>
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* TTI Request Logs Modal */}
        {viewingLogsItemId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-semibold">
                  TTI Request Logs - Device: {items.find(i => i.id === viewingLogsItemId)?.device_id}
                </h2>
                <button
                  onClick={() => setViewingLogsItemId(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <TTIRequestLogViewer
                  batchImportId={batchId}
                  batchImportItemId={viewingLogsItemId}
                />
              </div>
              <div className="p-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setViewingLogsItemId(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center space-x-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-gray-700">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BatchImportDetail;
